import React from "react";
import { Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import { pagesConfig } from "@corporate/pages.config";
import { Toaster } from "@corporate/components/ui/toaster";

const { Pages, Layout, mainPage } = pagesConfig;
const mainKey = mainPage || Object.keys(Pages)[0];

// Keeps a single bad record / render error from white-screening the whole section.
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidUpdate(prev) {
    if (prev.routeKey !== this.props.routeKey && this.state.error) {
      this.setState({ error: null });
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div className="p-10">
          <div className="max-w-lg mx-auto text-center bg-white/70 rounded-2xl border border-white/40 p-8 shadow-sm">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-800">This screen hit an error</h2>
            <p className="text-sm text-slate-500 mt-2">
              Something on this page failed to render. The rest of the Corporate workspace is still available.
            </p>
            <Link
              to="/corporate/Dashboard"
              className="inline-block mt-5 px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function LayoutWithLocation({ children }) {
  const location = useLocation();
  const seg = location.pathname.replace(/^\/corporate\/?/, "").split("/")[0] || mainKey;
  return <Layout currentPageName={seg || mainKey}>{children}</Layout>;
}

function CorporateRoutes() {
  const location = useLocation();
  const routeKey = location.pathname;
  return (
    <RouteErrorBoundary routeKey={routeKey}>
      <Routes>
        <Route index element={<Navigate to={mainKey} replace />} />
        {Object.entries(Pages).map(([name, Page]) => (
          <Route key={name} path={name} element={<Page />} />
        ))}
        <Route path="*" element={<Navigate to={mainKey} replace />} />
      </Routes>
    </RouteErrorBoundary>
  );
}

// Corporate ERP section, mounted at /corporate/* behind the existing CITIDAN login.
export default function CorporateApp() {
  const { user } = useAuth();

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    );
  }
  if (!user) return <Login />;

  return (
    <>
      <LayoutWithLocation>
        <CorporateRoutes />
      </LayoutWithLocation>
      <Toaster />
    </>
  );
}
